import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  SlidersHorizontal,
  TriangleAlert,
  Move,
} from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { TipoDeMovimiento, Variante, Movimiento } from '@/api/tienda.api';
import { DataTable, type Columna } from '@/components/datos/DataTable';
import { ConDetalle } from '@/components/datos/DetailDrawer';
import { Reposicion } from '@/components/tienda/Reposicion';
import { EntityLink } from '@/components/datos/EntityLink';
import { useAbrirDetalle } from '@/components/datos/contexto-detalle';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { VistasGuardadas } from '@/components/datos/VistasGuardadas';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { enRango, opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { dinero, numero, fecha, haceCuanto } from '@/lib/formato-tienda';

/**
 * INVENTARIO — existencia por talla, y el libro que la explica.
 *
 * ═══ POR QUÉ NO HAY UN CAMPO «EXISTENCIA» EDITABLE ═══
 *
 * La existencia no se escribe: se MUEVE. Toda entrada, salida, merma y ajuste
 * queda en el libro con su motivo y su fecha. Si hubiera un campo editable
 * habría dos caminos para cambiar el stock y el libro dejaría de ser la
 * verdad — y un inventario donde no se puede reconstruir qué pasó es una
 * planilla, no un inventario.
 *
 * Por eso un ajuste y una merma EXIGEN motivo: dentro de un mes nadie se va a
 * acordar de si esas tres piezas se perdieron, se regalaron o nunca existieron.
 */

type FilaDeExistencia = Variante & {
  producto: {
    id: string;
    name: string;
    sku: string | null;
    category?: string | null;
    /** Precio y costo DEL PRODUCTO: la talla hereda los suyos si no los tiene. */
    price?: number | null;
    cost?: number | null;
  };
};

/**
 * EL PRECIO Y EL COSTO QUE MANDAN.
 *
 * La talla puede tener los suyos; si no, hereda los del producto. Es la misma
 * regla que usa `valorDelInventario` en el servidor (`v.cost ?? p.cost`), y
 * tiene que ser la misma o esta pantalla diría «sin costo» de piezas que
 * Finanzas sí está contando. Dos números distintos para lo mismo es peor que
 * no tener ninguno: obliga a elegir cuál creer.
 */
const precioDe = (v: FilaDeExistencia): number | null => v.price ?? v.producto.price ?? null;
const costoDe = (v: FilaDeExistencia): number | null => v.cost ?? v.producto.cost ?? null;

const VISTAS = [
  { id: 'existencias', etiqueta: 'Existencias' },
  { id: 'por-acabarse', etiqueta: 'Por acabarse', ayuda: 'Debajo de su aviso' },
  { id: 'estancados', etiqueta: 'Estancados', ayuda: 'Sin salir hace tiempo' },
  { id: 'libro', etiqueta: 'Libro' },
  // El inventario autogestionable: lo que se va a agotar, sus suplidores y los pedidos.
  { id: 'reposicion', etiqueta: 'Reposición', ayuda: 'Qué pedir y a quién' },
] as const;

const TIPOS: Array<{ valor: TipoDeMovimiento; etiqueta: string }> = [
  { valor: 'ENTRADA', etiqueta: 'Entrada — llegó mercancía' },
  { valor: 'SALIDA', etiqueta: 'Salida — se fue sin factura' },
  { valor: 'MERMA', etiqueta: 'Merma — se dañó o se perdió' },
  { valor: 'AJUSTE', etiqueta: 'Ajuste — conteo físico' },
];

const ICONO_POR_TIPO: Record<TipoDeMovimiento, typeof ArrowDownCircle> = {
  ENTRADA: ArrowDownCircle,
  SALIDA: ArrowUpCircle,
  MERMA: TriangleAlert,
  AJUSTE: SlidersHorizontal,
};

const COLOR_POR_TIPO: Record<TipoDeMovimiento, string> = {
  ENTRADA: 'text-green-600 dark:text-green-400',
  SALIDA: 'text-red-600 dark:text-red-400',
  MERMA: 'text-amber-600 dark:text-amber-400',
  AJUSTE: 'text-slate-500',
};

export default function Inventario() {
  return (
    <ConDetalle>
      <Contenido />
    </ConDetalle>
  );
}
function Contenido() {
  const abrir = useAbrirDetalle();
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(
    { vista: 'existencias', buscar: '', dias: '30', categoria: '', existencia: '', tipo: '', desde: '', hasta: '' },
    { vista: VISTAS.map((v) => v.id), dias: ['15', '30', '60', '90'], existencia: ['con', 'agotadas'], tipo: TIPOS.map((t) => t.valor) },
  );
  const [mover, setMover] = useState<{ id: string; nombre: string; stock: number } | null>(null);

  const productos = useQuery({
    queryKey: ['productos', 'todos'],
    queryFn: () => tienda.listarProductos({ limit: 200 }),
  });

  const porAcabarse = useQuery({
    queryKey: ['inventario', 'bajo-stock'],
    queryFn: () => tienda.bajoStock(),
  });

  const estancados = useQuery({
    queryKey: ['inventario', 'estancados', filtros.dias],
    queryFn: () => tienda.estancados(Number(filtros.dias)),
    enabled: filtros.vista === 'estancados',
  });

  const libro = useQuery({
    queryKey: ['inventario', 'libro'],
    queryFn: () => tienda.listarMovimientos({ limit: 200 }),
    enabled: filtros.vista === 'libro',
  });

  /** Todas las variantes, aplanadas: el producto se lleva dentro para poder abrirlo. */
  const existencias = useMemo<FilaDeExistencia[]>(
    () =>
      (productos.data ?? []).flatMap((p) =>
        p.variants.map((v) => ({
          ...v,
          producto: {
            id: p.id,
            name: p.name,
            sku: p.sku,
            category: p.category,
            price: p.price,
            cost: p.cost,
          },
        })),
      ),
    [productos.data],
  );

  const piezas = existencias.reduce((a, v) => a + Math.max(0, v.stock), 0);
  const agotadas = existencias.filter((v) => v.stock <= 0).length;
  // El costo `null` es DESCONOCIDO, no cero: una paca sin repartir todavía no
  // sabe cuánto costó cada pieza. Sumarla como 0 diría que la tienda tiene
  // mercancía gratis, y ese número termina en una decisión de precio.
  const sinCosto = existencias.filter((v) => v.stock > 0 && costoDe(v) == null).length;
  const valor = existencias.reduce((a, v) => a + Math.max(0, v.stock) * (costoDe(v) ?? 0), 0);

  const q = filtros.buscar.trim().toLowerCase();
  const coincide = (nombre: string, sku: string | null, label: string) =>
    !q || `${nombre} ${sku ?? ''} ${label}`.toLowerCase().includes(q);

  const columnasDeExistencia: Columna<FilaDeExistencia>[] = [
    {
      id: 'producto',
      titulo: 'Producto',
      fija: true,
      valor: (v) => v.producto.name,
      celda: (v) => (
        <EntityLink
          tipo="producto"
          id={v.producto.id}
          etiqueta={v.producto.name}
          detalle={v.producto.sku ?? undefined}
        />
      ),
    },
    {
      id: 'talla',
      titulo: 'Talla',
      ancho: 'w-24',
      valor: (v) => v.label,
      celda: (v) => <Badge variant="info">{v.label}</Badge>,
    },
    {
      id: 'categoria',
      titulo: 'Categoría',
      ocultaPorDefecto: true,
      valor: (v) => v.producto.category ?? '',
      celda: (v) => (
        <span className="text-slate-600 dark:text-slate-300">{v.producto.category ?? '—'}</span>
      ),
    },
    {
      id: 'stock',
      titulo: 'Quedan',
      numerica: true,
      valor: (v) => v.stock,
      celda: (v) =>
        v.stock <= 0 ? (
          <Badge variant="danger">Agotada</Badge>
        ) : v.stock <= v.lowStockThreshold ? (
          <Badge variant="warning">{v.stock}</Badge>
        ) : (
          <span className="tabular-nums text-slate-900 dark:text-slate-100">{v.stock}</span>
        ),
      total: (fs) => numero(fs.reduce((a, v) => a + Math.max(0, v.stock), 0)),
    },
    {
      id: 'aviso',
      titulo: 'Aviso en',
      numerica: true,
      ocultaPorDefecto: true,
      valor: (v) => v.lowStockThreshold,
      celda: (v) => <span className="tabular-nums text-slate-500">{v.lowStockThreshold}</span>,
    },
    {
      id: 'precio',
      titulo: 'Precio',
      numerica: true,
      valor: (v) => precioDe(v) ?? 0,
      celda: (v) => {
        const p = precioDe(v);
        if (p == null) return <span className="text-amber-600 dark:text-amber-500">sin precio</span>;
        // Se marca cuando el precio es heredado: si alguien lo cambia en el
        // producto, cambia el de todas estas tallas a la vez.
        return v.price == null ? (
          <span title="Hereda el precio del producto" className="text-slate-600 dark:text-slate-300">
            {dinero(p)}
          </span>
        ) : (
          dinero(p)
        );
      },
    },
    {
      id: 'costo',
      titulo: 'Costo',
      numerica: true,
      valor: (v) => costoDe(v) ?? -1,
      celda: (v) => {
        const c = costoDe(v);
        return c == null ? (
          <span
            title="Todavía no se repartió el costo de su compra"
            className="text-amber-600 dark:text-amber-500"
          >
            sin costo
          </span>
        ) : (
          dinero(c)
        );
      },
    },
    {
      id: 'valor',
      titulo: 'Plata parada',
      numerica: true,
      valor: (v) => Math.max(0, v.stock) * (costoDe(v) ?? 0),
      celda: (v) => {
        const c = costoDe(v);
        return c == null ? (
          <span className="text-slate-400">—</span>
        ) : (
          dinero(Math.max(0, v.stock) * c)
        );
      },
      total: (fs) => dinero(fs.reduce((a, v) => a + Math.max(0, v.stock) * (costoDe(v) ?? 0), 0)),
    },
  ];

  const columnasDeLibro: Columna<Movimiento>[] = [
    {
      id: 'cuando',
      titulo: 'Cuándo',
      fija: true,
      valor: (m) => m.createdAt,
      celda: (m) => (
        <span title={fecha(m.createdAt, true)} className="text-slate-600 dark:text-slate-300">
          {haceCuanto(m.createdAt)}
        </span>
      ),
    },
    {
      id: 'tipo',
      titulo: 'Qué pasó',
      ancho: 'w-32',
      valor: (m) => m.tipo,
      celda: (m) => {
        const Icono = ICONO_POR_TIPO[m.tipo];
        return (
          <span className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-200">
            <Icono className={`h-4 w-4 ${COLOR_POR_TIPO[m.tipo]}`} />
            {m.tipo.charAt(0) + m.tipo.slice(1).toLowerCase()}
          </span>
        );
      },
    },
    {
      id: 'producto',
      titulo: 'Producto',
      valor: (m) => m.productVariant.product.name,
      celda: (m) => (
        <EntityLink
          tipo="producto"
          id={m.productVariant.product.id}
          etiqueta={m.productVariant.product.name}
          detalle={m.productVariant.label}
        />
      ),
    },
    {
      id: 'cantidad',
      titulo: 'Piezas',
      numerica: true,
      valor: (m) => m.cantidad,
      celda: (m) => (
        <span className={`tabular-nums font-semibold ${COLOR_POR_TIPO[m.tipo]}`}>
          {m.tipo === 'ENTRADA' ? '+' : m.tipo === 'AJUSTE' ? '' : '−'}
          {Math.abs(m.cantidad)}
        </span>
      ),
    },
    {
      id: 'motivo',
      titulo: 'Motivo',
      valor: (m) => m.motivo ?? '',
      celda: (m) =>
        m.saleId ? (
          <EntityLink tipo="venta" id={m.saleId} etiqueta={m.motivo ?? 'venta'} />
        ) : (
          <span className="text-slate-600 dark:text-slate-300">{m.motivo ?? '—'}</span>
        ),
    },
  ];


  const esLibro = filtros.vista === 'libro';
  const fuente: FilaDeExistencia[] =
    filtros.vista === 'por-acabarse'
      ? (porAcabarse.data ?? []).map((v) => ({ ...v, producto: v.product }))
      : filtros.vista === 'estancados'
        ? (estancados.data ?? []).map((v) => ({ ...v, producto: v.product }))
        : existencias;

  // SUPERFILTRO (Josué, 14-oct). Las pestañas de arriba se quedan: cambian QUÉ se mira (otra
  // consulta, otras columnas); lo que recorta esas filas va en la barra.
  const filas = fuente.filter(
    (v) =>
      coincide(v.producto.name, v.producto.sku, v.label) &&
      (!filtros.categoria || (v.producto.category ?? '') === filtros.categoria) &&
      (filtros.existencia !== 'con' || v.stock > 0) &&
      (filtros.existencia !== 'agotadas' || v.stock <= 0),
  );
  const todosLosMovimientos = libro.data ?? [];
  const movimientos = todosLosMovimientos.filter(
    (m) =>
      coincide(m.productVariant.product.name, m.productVariant.product.sku, m.productVariant.label) &&
      (!filtros.tipo || m.tipo === filtros.tipo) &&
      enRango(m.createdAt, filtros.desde, filtros.hasta),
  );
  const facetasDeExistencia: Faceta[] = [
    { clave: 'categoria', titulo: 'Categoría', todas: 'Todas', opciones: opcionesDe(fuente, (v) => v.producto.category) },
    {
      clave: 'existencia',
      titulo: 'Existencia',
      todas: 'Todas',
      opciones: [
        { valor: 'con', etiqueta: 'Con piezas', cuenta: fuente.filter((v) => v.stock > 0).length },
        { valor: 'agotadas', etiqueta: 'Agotadas', cuenta: fuente.filter((v) => v.stock <= 0).length },
      ],
    },
  ];
  const facetasDelLibro: Faceta[] = [
    { clave: 'tipo', titulo: 'Movimiento', opciones: TIPOS.map((t) => ({ valor: t.valor, etiqueta: t.etiqueta.split(' — ')[0]!, cuenta: todosLosMovimientos.filter((m) => m.tipo === t.valor).length })) },
    { tipo: 'fechas', clave: 'fecha', titulo: 'Fecha', desde: 'desde', hasta: 'hasta', hastaHoy: true },
  ];

  const consulta =
    filtros.vista === 'por-acabarse'
      ? porAcabarse
      : filtros.vista === 'estancados'
        ? estancados
        : productos;

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Inventario"
        subtitulo="La existencia no se escribe: se mueve. Todo lo que entra y sale queda en el libro."
        kpis={
          <>
            <Kpi
              titulo="Piezas en existencia"
              valor={numero(piezas)}
              pie={`${existencias.length} tallas`}
              onClick={() => poner('vista', 'existencias')}
            />
            <Kpi
              titulo="Plata parada"
              valor={dinero(valor)}
              pie={sinCosto > 0 ? `${sinCosto} tallas sin costo, no cuentan` : 'a costo'}
            />
            <Kpi
              titulo="Por acabarse"
              valor={numero(porAcabarse.data?.length ?? 0)}
              tono={(porAcabarse.data?.length ?? 0) > 0 ? 'malo' : 'normal'}
              bajarEsBueno
              onClick={() => poner('vista', 'por-acabarse')}
            />
            <Kpi
              titulo="Agotadas"
              valor={numero(agotadas)}
              pie="no se pueden ofrecer"
              tono={agotadas > 0 ? 'malo' : 'normal'}
              bajarEsBueno
            />
          </>
        }
      />

      <VistasGuardadas
        vistas={VISTAS.map((v) => ({
          ...v,
          cuenta:
            v.id === 'existencias'
              ? existencias.length
              : v.id === 'por-acabarse'
                ? (porAcabarse.data?.length ?? 0)
                : undefined,
        }))}
        activa={filtros.vista}
        onCambiar={(v) => poner('vista', v)}
      />

      {filtros.vista === 'reposicion' ? (
        <Reposicion />
      ) : esLibro ? (
        <DataTable
          filas={movimientos}
          columnas={columnasDeLibro}
          idDe={(m) => m.id}
          cargando={libro.isLoading}
          error={libro.isError ? 'No se pudo cargar el libro' : null}
          onReintentar={() => void libro.refetch()}
          buscar={filtros.buscar}
          onBuscar={(v) => poner('buscar', v)}
          placeholderBuscar="Busca por producto, código o talla…"
          facetas={facetasDelLibro}
          valoresDeFacetas={filtros}
          onFacetas={ponerVarios}
          onLimpiarFiltros={() => ponerVarios(sinFiltros(facetasDelLibro))}
          resultados={textoDeResultados(movimientos.length, todosLosMovimientos.length, ['movimiento', 'movimientos'])}
          memoria="inventario-libro"
          exportarComo="libro-de-inventario"
          vacio={{
            titulo: 'El libro está vacío',
            descripcion: 'Aquí va a quedar todo lo que entre y salga, con su motivo y su fecha.',
          }}
        />
      ) : (
        <DataTable
          filas={filas}
          columnas={columnasDeExistencia}
          idDe={(v) => v.id}
          cargando={consulta.isLoading}
          error={consulta.isError ? 'No se pudo cargar' : null}
          onReintentar={() => void consulta.refetch()}
          buscar={filtros.buscar}
          onBuscar={(v) => poner('buscar', v)}
          placeholderBuscar="Busca por producto, código o talla…"
          facetas={facetasDeExistencia}
          valoresDeFacetas={filtros}
          onFacetas={ponerVarios}
          onLimpiarFiltros={() => ponerVarios(sinFiltros(facetasDeExistencia))}
          resultados={textoDeResultados(filas.length, fuente.length, ['talla', 'tallas'])}
          memoria="inventario-existencias"
          exportarComo="existencias"
          barra={
            filtros.vista === 'estancados' ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Sin venderse en</span>
                <Select
                  value={filtros.dias}
                  onChange={(e) => poner('dias', e.target.value)}
                  className="w-28"
                >
                  <option value="15">15 días</option>
                  <option value="30">30 días</option>
                  <option value="60">60 días</option>
                  <option value="90">90 días</option>
                </Select>
              </div>
            ) : undefined
          }
          onAbrirFila={(v) =>
            abrir?.({
              tipo: 'producto',
              id: v.producto.id,
              etiqueta: v.producto.name,
              detalle: v.label,
            })
          }
          accionesDeFila={[
            {
              etiqueta: 'Mover existencia',
              icono: <Move className="h-3.5 w-3.5" />,
              onClick: (v) =>
                setMover({
                  id: v.id,
                  nombre: `${v.producto.name} · ${v.label}`,
                  stock: v.stock,
                }),
            },
          ]}
          vacio={{
            titulo:
              filtros.vista === 'por-acabarse'
                ? 'Nada por acabarse'
                : filtros.vista === 'estancados'
                  ? 'Nada estancado'
                  : 'Todavía no hay existencia',
            descripcion:
              filtros.vista === 'por-acabarse'
                ? 'Ninguna talla está por debajo de su aviso.'
                : filtros.vista === 'estancados'
                  ? `Todo lo que hay se movió en los últimos ${filtros.dias} días.`
                  : 'Carga el inventario desde Productos y aparece aquí.',
          }}
        />
      )}

      {mover && (
        <ModalDeMovimiento
          varianteId={mover.id}
          nombre={mover.nombre}
          stock={mover.stock}
          onCerrar={() => setMover(null)}
        />
      )}
    </div>
  );
}

function ModalDeMovimiento({
  varianteId,
  nombre,
  stock,
  onCerrar,
}: {
  varianteId: string;
  nombre: string;
  stock: number;
  onCerrar: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [tipo, setTipo] = useState<TipoDeMovimiento>('ENTRADA');
  const [cantidad, setCantidad] = useState('');
  const [motivo, setMotivo] = useState('');

  const guardar = useMutation({
    mutationFn: () =>
      tienda.registrarMovimiento({
        productVariantId: varianteId,
        tipo,
        cantidad: Number(cantidad),
        motivo: motivo.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Movimiento registrado');
      void qc.invalidateQueries({ queryKey: ['inventario'] });
      void qc.invalidateQueries({ queryKey: ['productos'] });
      onCerrar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo registrar'),
  });

  // El ajuste y la merma EXIGEN motivo. No es un capricho del formulario: lo
  // exige también el servicio, y aquí se dice antes para no hacer ir y volver.
  const exigeMotivo = tipo === 'AJUSTE' || tipo === 'MERMA';
  const faltaMotivo = exigeMotivo && !motivo.trim();
  const n = Number(cantidad);
  const listo = cantidad !== '' && n !== 0 && !faltaMotivo;

  // Aviso antes de mandar: el trigger de la base no deja bajar de cero, y es
  // mejor decirlo aquí que devolver un error que parece una falla del sistema.
  const seVaBajoCero =
    (tipo === 'SALIDA' || tipo === 'MERMA') && n > stock
      ? `Solo quedan ${stock}. La base no va a dejar bajar de cero.`
      : tipo === 'AJUSTE' && n < 0 && Math.abs(n) > stock
        ? `Solo quedan ${stock}. Un ajuste de ${n} dejaría la existencia negativa.`
        : null;

  return (
    <Modal open onClose={onCerrar} title="Mover existencia">
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {nombre} · <strong>quedan {stock}</strong>
        </p>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Qué pasó
          </label>
          <Select value={tipo} onChange={(e) => setTipo(e.target.value as TipoDeMovimiento)}>
            {TIPOS.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.etiqueta}
              </option>
            ))}
          </Select>
          {tipo === 'MERMA' && (
            <p className="mt-1 text-xs text-slate-500">
              La merma no es una venta: baja la existencia pero no suma nada al vendido.
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Cantidad
          </label>
          <Input
            type="number"
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
            placeholder={tipo === 'AJUSTE' ? 'ej. -2 si faltan dos' : 'ej. 6'}
          />
          {tipo === 'AJUSTE' && (
            <p className="mt-1 text-xs text-slate-500">
              En un ajuste el signo importa: negativo si contaste menos de lo que dice el sistema.
            </p>
          )}
          {seVaBajoCero && <p className="mt-1 text-xs text-red-600">{seVaBajoCero}</p>}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Motivo {exigeMotivo && <span className="text-red-600">*</span>}
          </label>
          <Input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder={
              tipo === 'AJUSTE'
                ? 'conteo físico del sábado'
                : tipo === 'MERMA'
                  ? 'se manchó en la mudanza'
                  : 'llegó la tanda de la China'
            }
          />
          {faltaMotivo && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-500">
              Sin motivo es un descuadre sin dueño. En un mes nadie va a saber qué pasó.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} disabled={!listo || guardar.isPending}>
            {guardar.isPending ? 'Registrando…' : 'Registrar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
